import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { money, fmtDate, TREATMENTS } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Plus, ClipboardList, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

const emptyForm = () => ({ description: "", amount: "", due_date: "", category: "", treatment: "standard", notes: "" });

export default function BillsPage() {
  const [bills, setBills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [payOpen, setPayOpen] = useState(false);
  const [payBill, setPayBill] = useState(null);
  const [payForm, setPayForm] = useState({ account_id: "", date: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    const [b, c, a] = await Promise.all([api.get("/bills"), api.get("/categories"), api.get("/bank-accounts")]);
    setBills(b.data); setCategories(c.data); setAccounts(a.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.description || !form.amount) { toast.error("Enter a description and amount"); return; }
    await api.post("/bills", { ...form, amount: parseFloat(form.amount) || 0 });
    toast.success("Bill added"); setOpen(false); setForm(emptyForm()); load();
  };
  const remove = async (id) => { await api.delete(`/bills/${id}`); toast.success("Removed"); load(); };
  const openPay = (b) => { setPayBill(b); setPayForm({ account_id: "", date: new Date().toISOString().slice(0, 10) }); setPayOpen(true); };
  const submitPay = async () => {
    await api.post(`/bills/${payBill.id}/pay`, { account_id: payForm.account_id, date: payForm.date });
    toast.success("Marked paid — expense created"); setPayOpen(false); load();
  };

  const outstanding = bills.filter((b) => b.status === "outstanding");
  const paid = bills.filter((b) => b.status === "paid");
  const totalOut = outstanding.reduce((s, b) => s + (b.amount || 0), 0);
  const today = new Date().toISOString().slice(0, 10);

  const Row = ({ b }) => {
    const overdue = b.status === "outstanding" && b.due_date && b.due_date < today;
    return (
      <div className="flex items-center justify-between px-4 py-3 row-hover" data-testid={`bill-row-${b.id}`}>
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{b.description}</div>
          <div className="text-xs text-muted-foreground">
            {b.category || "Uncategorised"} · {TREATMENTS[b.treatment]?.short}
            {b.due_date && <> · due {fmtDate(b.due_date)}</>}
            {overdue && <span className="text-rose-600 font-medium inline-flex items-center gap-1 ml-1"><AlertTriangle className="w-3 h-3" /> overdue</span>}
            {b.status === "paid" && <span className="text-emerald-600 ml-1">· paid {fmtDate(b.paid_date)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="num font-semibold text-sm">{money(b.amount)}</span>
          {b.status === "outstanding" ? (
            <Button size="sm" variant="outline" onClick={() => openPay(b)} data-testid={`pay-bill-${b.id}`} className="h-8 gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark paid
            </Button>
          ) : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
          <button onClick={() => remove(b.id)} data-testid={`delete-bill-${b.id}`} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Bills to Pay</h1>
          <p className="text-muted-foreground text-sm mt-1">Track one-off supplier bills (milk, bread…) — separate from automated fixed costs.</p>
        </div>
        <Button onClick={() => { setForm(emptyForm()); setOpen(true); }} data-testid="add-bill-btn" className="gap-2"><Plus className="w-4 h-4" /> Add bill</Button>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 mb-6 flex items-center justify-between" data-testid="bills-outstanding-total">
        <span className="text-sm font-medium">Outstanding to pay ({outstanding.length})</span>
        <span className="num text-xl font-bold text-rose-600">{money(totalOut)}</span>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden mb-6">
        <div className="px-4 py-3 bg-secondary/50 font-heading font-semibold text-sm flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Outstanding</div>
        <div className="divide-y divide-border">
          {outstanding.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground" data-testid="bills-empty">Nothing outstanding. 🎉</div> : outstanding.map((b) => <Row key={b.id} b={b} />)}
        </div>
      </div>

      {paid.length > 0 && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 bg-secondary/50 font-heading font-semibold text-sm">Paid</div>
          <div className="divide-y divide-border">{paid.map((b) => <Row key={b.id} b={b} />)}</div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="bill-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New bill</DialogTitle>
            <DialogDescription>A bill you owe — mark it paid once the money goes out.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" data-testid="bill-description" placeholder="Milk — More Than Milk Co" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (incl. GST)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1.5" data-testid="bill-amount" placeholder="0.00" /></div>
              <div><Label>Due date <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5" data-testid="bill-due" /></div>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => { set("category", v); const c = categories.find((x) => x.name === v); if (c) set("treatment", c.treatment); }}>
                <SelectTrigger className="mt-1.5" data-testid="bill-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{categories.filter((c) => c.type === "expense").map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-bill-btn">Add bill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="sm:max-w-md" data-testid="pay-bill-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark bill paid</DialogTitle>
            <DialogDescription>Creates an expense for {payBill ? money(payBill.amount) : ""} and clears the bill.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div>
              <Label>Paid from</Label>
              <Select value={payForm.account_id || "none"} onValueChange={(v) => setPayForm((f) => ({ ...f, account_id: v === "none" ? "" : v }))}>
                <SelectTrigger className="mt-1.5" data-testid="pay-account"><SelectValue placeholder="Cash / not set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Cash / not set</SelectItem>
                  {accounts.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date paid</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm((f) => ({ ...f, date: e.target.value }))} className="mt-1.5" data-testid="pay-date" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={submitPay} data-testid="confirm-pay-btn">Mark paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
